import { Input } from "@/components/input";
import { useTracksBuffer } from "@/services/buffer-controller";
import { Cross2Icon, GearIcon } from "@radix-ui/react-icons";
import {
  Popover,
  ScrollArea,
  Theme,
  IconButton,
  Button,
} from "@radix-ui/themes";
import { FormEventHandler, useState } from "react";
import { toast } from "react-hot-toast";
import { useLocalStorage } from "react-use";

const defaultConfig = {
  startOffset: 2000,
  chunkSize: 2000,
  pollingInterval: 500,
  retryInterval: 500,
};

export function Preferences() {
  const [tracksBufferClient] = useTracksBuffer();
  const [persistedConfig, saveConfig] = useLocalStorage(
    "config",
    defaultConfig
  );
  const [open, setOpen] = useState(false);

  const onReset = () => {
    tracksBufferClient.updateConfig(defaultConfig);
    saveConfig(defaultConfig);

    toast.success("Preference settings are reset");
    setOpen(false);
  };

  const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    const newConfig = {
      startOffset: Number(formData.get("startOffset")),
      chunkSize: Number(formData.get("chunkSize")),
      pollingInterval: Number(formData.get("pollingInterval")),
      retryInterval: Number(formData.get("retryInterval")),
    };
    tracksBufferClient.updateConfig(newConfig);
    saveConfig(newConfig);

    toast.success("Preference changes are saved");
    setOpen(false);
  };

  return (
    <div>
      <div className="flex items-center">
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger>
            <Button variant="ghost" color="gray" size="1">
              Preferences
            </Button>
          </Popover.Trigger>
          <Popover.Content className="p-0">
            <Theme>
              <div className="flex items-center gap-2 p-2 leading-none border-b ">
                <GearIcon className="m-1" />
                <span className="font-medium">Preferences</span>
                <span className="grow"></span>
                <Popover.Close aria-label="Close">
                  <IconButton variant="soft" color="gray">
                    <Cross2Icon width={16} height={16} />
                  </IconButton>
                </Popover.Close>
              </div>
              <div className="w-56">
                <ScrollArea className="max-h-[300px]">
                  <div className="p-2">
                    <div className="flex items-center gap-2 mb-2 leading-none">
                      <span className="font-medium">Streaming</span>
                    </div>
                    <form id="preferences" onSubmit={onSubmit}>
                      <div className="flex flex-col gap-2">
                        <Input
                          type="number"
                          min={0}
                          name="startOffset"
                          label="Live Start Offset (ms)"
                          className="h-10"
                          defaultValue={persistedConfig?.startOffset || 0}
                        />
                        <Input
                          type="number"
                          min={0}
                          name="chunkSize"
                          label="Chunk size (ms)"
                          className="h-10"
                          defaultValue={persistedConfig?.chunkSize || 0}
                        />
                        <Input
                          type="number"
                          min={0}
                          name="pollingInterval"
                          label="Polling interval (ms)"
                          className="h-10"
                          defaultValue={persistedConfig?.pollingInterval || 0}
                        />
                        <Input
                          type="number"
                          min={0}
                          name="retryInterval"
                          label="Retry interval (ms)"
                          className="h-10"
                          defaultValue={persistedConfig?.retryInterval || 0}
                        />
                      </div>
                    </form>
                  </div>
                </ScrollArea>
              </div>
              <div className="p-2 flex justify-between border-t">
                <Button
                  variant="soft"
                  color="gray"
                  highContrast
                  onClick={onReset}
                >
                  Reset
                </Button>
                <Button
                  variant="soft"
                  color="gray"
                  highContrast
                  form="preferences"
                >
                  Apply
                </Button>
              </div>
            </Theme>
          </Popover.Content>
        </Popover.Root>
      </div>
    </div>
  );
}
